import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Checkbox } from "@progress/kendo-react-inputs";
import { filterBy, CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { uploadIcon, trashIcon } from "@progress/kendo-svg-icons";

import { useInjection } from "@presentation/components/dependencyInjection";
import { UploadSettingsDialog } from "@presentation/components/UploadSettingsDialog";
import { ListSoftwareSettingsService, DeleteSoftwareSettingsService } from "@application/useCases";
import { SoftwareSettingsMetadata, SettingsTypeEnumSchema, SoftwareEnumSchema } from "@domain/schemas/BI";
import { getSoftwareAccent } from "@domain/software";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { markItemsAsLastUsed, sortItemsByLastUsed } from "@presentation/common/utils/lastUsedOrdering";
import { fetchSettingsListCached, setCachedSettingsList } from "./settingsListCache";
import { useShouldShowEngineName } from "./useLicensedSoftware";
import { useShallow } from "zustand/react/shallow";

interface GenericMultiSelectSettingsFormProperties {
  settingsType: SettingsTypeEnumSchema;
  /**
  Supplied by the software profile; falls back to the historical per-type defaults.
  */
  selection?: "single" | "multi";
  /**
  When false the files are shared across engines, so no filtering or attribution applies.
  */
  softwareScoped?: boolean;
  placeholder?: string;
  uploadButtonTitle?: string;
}

const DEFAULT_SINGLE_SELECTION_TYPES = new Set(["Experiment", "ConditionSetup", "Modification", "SpectroMineSearch"]);

const normalizePath = (path?: string | null) => (path ?? "").replaceAll("\\", "/").replaceAll(/^\/+|\/+$/g, "");
const toFullPath = (location?: string | null, name?: string | null) => normalizePath([location, name].filter(Boolean).join("/"));

/**
/**
 * Neutral palette for settings shared across search engines.
 *
 * Deliberately grey: any saturated colour here would read as "yet another
 * engine" next to the blue/violet engine accents.
 */
const SHARED_ACCENT = { background: "#f1f5f9", border: "#94a3b8", text: "#475569" };

const PILL_STYLE: React.CSSProperties = {
  flexShrink: 0,
  padding: "1px 7px",
  borderRadius: "999px",
  fontSize: "11px",
  lineHeight: "16px",
  fontWeight: 600,
  border: "1px solid",
  whiteSpace: "nowrap",
};

/**
 * Shows which search engine a settings file was uploaded for.
 *
 * Always carries the engine name as text: the colour alone would not be
 * perceivable for every user, and this is a correctness signal rather than
 * decoration. Files predating per-engine settings are reported as Spectronaut by
 * the backend, since it was the only engine at the time.
 */
const SettingsOriginPill: React.FC<{ software?: SoftwareEnumSchema | null; showEngineName: boolean }> = ({ software, showEngineName }) => {
  if (!showEngineName) return null;
  const resolvedSoftware = software ?? ("Spectronaut" as SoftwareEnumSchema);
  const accent = getSoftwareAccent(resolvedSoftware);
  return (
    <span style={{ ...PILL_STYLE, backgroundColor: accent.background, borderColor: accent.border, color: accent.text }} title={`Uploaded for ${resolvedSoftware}`}>
      {resolvedSoftware}
    </span>
  );
};

export const GenericMultiSelectSettingsForm: React.FC<GenericMultiSelectSettingsFormProperties> = ({
  settingsType,
  selection,
  softwareScoped = true,
  placeholder = "Click to select files...",
  uploadButtonTitle = "Upload new file",
}) => {
  const isSingleSelection = selection ? selection === "single" : DEFAULT_SINGLE_SELECTION_TYPES.has(settingsType);
  const settingsLastUsedStorageKey = `job-setup-settings-last-used-${settingsType}`;
  const getSettingsStorageKey = useCallback((item: SoftwareSettingsMetadata) => toFullPath(item.location, item.name) || item.id, []);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsList, setSettingsList] = useState<SoftwareSettingsMetadata[]>([]);
  const [selectedSettings, setSelectedSettings] = useState<SoftwareSettingsMetadata[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSize, setDropdownSize] = useState({ width: 600, height: 400 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const softwareVersion = useJobSetupStore((state) => state.CreateJobState.softwareVersion);
  const software = useJobSetupStore((state) => state.CreateJobState.software);
  const showEngineName = useShouldShowEngineName();
  // Shared settings types are listed and cached across every engine, and must not
  // borrow the engine's colour or they would look engine-scoped.
  const scopedSoftware = softwareScoped ? software : undefined;
  const activeAccent = softwareScoped ? getSoftwareAccent(software) : SHARED_ACCENT;

  const dropdownReference = useRef<HTMLDivElement>(null);
  const multiSelectReference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);

  const { settingsDirectDIA, settingsSpectroMineSearch, reportSettings, goSettings, modificationRepository, conditionSetup, setInJobStore } = useJobSetupStore(
    useShallow((state) => ({
      settingsDirectDIA: state.settingsDirectDIA,
      settingsSpectroMineSearch: state.settingsSpectroMineSearch,
      reportSettings: state.reportSettings,
      goSettings: state.goSettings,
      modificationRepository: state.modificationRepository,
      conditionSetup: state.conditionSetup,
      setInJobStore: (() => {
        switch (settingsType) {
          case "Experiment": {
            return state.setDirectDIASettings;
          }
          case "SpectroMineSearch": {
            return state.setSpectroMineSearchSettings;
          }
          case "Report": {
            return state.setReportSettings;
          }
          case "GO": {
            return state.setGOSettings;
          }
          case "Modification": {
            return state.setModificationRepository;
          }
          case "ConditionSetup": {
            return state.setConditionSetup;
          }
          default: {
            throw new Error(`Unsupported settings type: ${settingsType}`);
          }
        }
      })(),
    })),
  );

  // Load persisted settings on component mount
  useEffect(() => {
    const getPersistedSettings = () => {
      switch (settingsType) {
        case "Experiment": {
          return settingsDirectDIA;
        }
        case "SpectroMineSearch": {
          return settingsSpectroMineSearch;
        }
        case "Report": {
          return reportSettings;
        }
        case "GO": {
          return goSettings;
        }
        case "Modification": {
          return modificationRepository;
        }
        case "ConditionSetup": {
          return conditionSetup;
        }
        default: {
          return [];
        }
      }
    };

    const persistedSettings = getPersistedSettings();
    if (!persistedSettings) return;

    const settingsArray = Array.isArray(persistedSettings) ? persistedSettings : [persistedSettings];
    const validSettings = settingsArray.filter((s) => s && s.id); // only keep valid objects
    if (validSettings.length > 0) {
      setSelectedSettings(validSettings);
    }
  }, [settingsType, settingsDirectDIA, settingsSpectroMineSearch, reportSettings, goSettings, modificationRepository, conditionSetup]);

  // Handle mouse events for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setDropdownPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }

      if (!isResizing) return;

      const rect = dropdownReference.current?.getBoundingClientRect();
      if (rect) {
        setDropdownSize({
          width: Math.max(300, e.clientX - rect.left),
          height: Math.max(200, e.clientY - rect.top),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  // Position dropdown below the multiselect
  useEffect(() => {
    if (!(dropdownOpen && multiSelectReference.current)) {
      return;
    }

    const rect = multiSelectReference.current.getBoundingClientRect();

    // Calculate initial position
    let x = rect.left;
    let y = rect.bottom + 5;

    // Constrain to window boundaries
    x = Math.max(10, Math.min(x, window.innerWidth - dropdownSize.width - 10));
    y = Math.max(10, Math.min(y, window.innerHeight - dropdownSize.height - 10));

    setDropdownPosition({ x, y });

    setTimeout(() => searchInputReference.current?.focus(), 0);
  }, [dropdownOpen, dropdownSize.height, dropdownSize.width]);

  // Engine-scoped types must never offer another engine's files. Files with no
  // recorded engine predate per-engine settings, when Spectronaut was the only
  // search engine, so they count as Spectronaut.
  const engineSettings = useMemo<SoftwareSettingsMetadata[]>(
    () => (softwareScoped && software ? settingsList.filter((item) => (item.software ?? "Spectronaut") === software) : settingsList),
    [settingsList, software, softwareScoped],
  );
  const filteredData = filter ? filterBy(engineSettings, filter) : engineSettings;
  const getVersionMetadata = useCallback((version?: string | null) => {
    const normalizedVersion = (version ?? "").trim().replace(/^(?:SN|SM)/i, "");
    const match = normalizedVersion.match(/^v?(\d+)\.\d+\.\d+(?:[.-].*)?$/i);
    return match ? { major: match[1] } : null;
  }, []);
  const getSettingsMajor = useCallback((item: SoftwareSettingsMetadata) => {
    const major = item.detectedMajorVersion;
    return major === null || major === undefined ? null : String(major);
  }, []);
  const getSettingsCompatibility = useCallback(
    (item: SoftwareSettingsMetadata) => {
      const selectedVersion = getVersionMetadata(softwareVersion);
      const major = getSettingsMajor(item);
      // Settings uploaded for another engine are never usable, whatever the major version.
      if (softwareScoped && software && item.software && item.software !== software) {
        return { compatible: false, reason: `Incompatible: uploaded for ${item.software}, selected ${software}` };
      }
      // Files without a detected version cannot be judged, so they stay selectable.
      if (!selectedVersion || !major) return { compatible: true, reason: "" };
      const engineLabel = software ?? "Spectronaut";
      const isCompatible = major === selectedVersion.major;
      const detectedLabel = showEngineName ? `${engineLabel} ${major}` : `version ${major}`;
      const selectedLabel = showEngineName ? `${engineLabel} ${selectedVersion.major}` : `version ${selectedVersion.major}`;
      return {
        compatible: isCompatible,
        reason: isCompatible ? `Compatible with ${selectedLabel}` : `Incompatible: detected ${detectedLabel}, selected ${selectedLabel}`,
      };
    },
    [getSettingsMajor, getVersionMetadata, showEngineName, software, softwareScoped, softwareVersion],
  );
  const sortSettings = useCallback(
    (items: SoftwareSettingsMetadata[]) => sortItemsByLastUsed(items, settingsLastUsedStorageKey, getSettingsStorageKey, (item) => item.name),
    [getSettingsStorageKey, settingsLastUsedStorageKey],
  );

  useEffect(() => {
    if (selectedSettings.length === 0) return;

    const compatibleSelection = selectedSettings.filter((setting) => getSettingsCompatibility(setting).compatible);
    if (compatibleSelection.length === selectedSettings.length) {
      return;
    }

    setSelectedSettings(compatibleSelection);
    setInJobStore(compatibleSelection);
  }, [getSettingsCompatibility, selectedSettings, setInJobStore, softwareVersion]);

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    const rect = dropdownReference.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  const openSettingsDialog = () => setShowSettingsDialog(true);
  const closeSettingsDialog = () => setShowSettingsDialog(false);

  const listSettingsService = useInjection(ListSoftwareSettingsService);
  const deleteSettingsService = useInjection(DeleteSoftwareSettingsService);

  useEffect(() => {
    let isCancelled = false;

    async function fetchSettingsList() {
      try {
        const list = await fetchSettingsListCached(
          async (type, engine) => {
            const response = await listSettingsService.getSettingsListAsync(type, engine);
            return response.list ?? [];
          },
          settingsType,
          scopedSoftware,
        );
        if (!isCancelled) {
          setSettingsList(sortSettings(list));
        }
      } catch (error) {
        console.error(`Error fetching settings list for ${settingsType}:`, error);
      }
    }
    fetchSettingsList();

    return () => {
      isCancelled = true;
    };
  }, [listSettingsService, settingsType, scopedSoftware, sortSettings]);

  const afterSettingsUploadSuccess = (uploadedSettings: SoftwareSettingsMetadata) => {
    markItemsAsLastUsed(settingsLastUsedStorageKey, [getSettingsStorageKey(uploadedSettings)]);
    const updatedList = [uploadedSettings, ...settingsList.filter((item) => item.id !== uploadedSettings.id)];
    setCachedSettingsList(settingsType, updatedList, scopedSoftware);
    setSettingsList(sortSettings(updatedList));
    setSelectedSettings([uploadedSettings]);
    setInJobStore([uploadedSettings]);
    setShowSettingsDialog(false);
  };

  const isMatchingSetting = (candidate: SoftwareSettingsMetadata, selected: SoftwareSettingsMetadata) => {
    return candidate.id === selected.id || toFullPath(candidate.location, candidate.name) === toFullPath(selected.location, selected.name);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} ref={multiSelectReference}>
        <div style={{ width: "70%", position: "relative", cursor: "pointer" }} onClick={() => setDropdownOpen(!dropdownOpen)}>
          {/* The outline carries the engine scope: accent-coloured when the files
              belong to one engine, grey when they are shared. The banner above the
              settings sections names the colour, and the tooltip repeats it in
              words for anyone who cannot rely on colour alone. */}
          <div
            title={softwareScoped && software ? (showEngineName ? `${software} settings only` : "Engine-specific settings only") : "Shared across search engines"}
            style={{
              border: `1px solid ${activeAccent.border}`,
              borderLeft: `4px solid ${activeAccent.border}`,
              borderRadius: "3px",
              padding: "8px",
              minHeight: "32px",
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "4px",
            }}
          >
            {selectedSettings.length > 0 ? (
              selectedSettings.map((setting) => (
                <span
                  key={setting.id}
                  style={{
                    backgroundColor: activeAccent.background,
                    border: `1px solid ${activeAccent.border}`,
                    borderRadius: "3px",
                    padding: "2px 8px",
                    fontSize: "12px",
                    color: activeAccent.text,
                  }}
                >
                  {setting.name}
                </span>
              ))
            ) : (
              <span style={{ color: "#999" }}>{placeholder}</span>
            )}
          </div>
        </div>

        <Button
          svgIcon={uploadIcon}
          onClick={openSettingsDialog}
          style={{
            width: "27%",
            borderRadius: "3px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          title={softwareScoped && software && showEngineName ? `${uploadButtonTitle} — saved as ${software} settings` : uploadButtonTitle}
        >
          {uploadButtonTitle}
        </Button>
      </div>

      {dropdownOpen && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "transparent",
              zIndex: 999,
            }}
            onClick={() => setDropdownOpen(false)}
          />
          <div
            ref={dropdownReference}
            style={{
              position: "fixed",
              left: dropdownPosition.x,
              top: dropdownPosition.y,
              width: dropdownSize.width,
              height: dropdownSize.height,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "3px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #e0e0e0",
                cursor: "move",
                userSelect: "none",
                borderRadius: "4px 4px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onMouseDown={handleDragStart}
            >
              <span style={{ fontWeight: "500", fontSize: "14px" }}>Select Settings</span>
              <button
                onClick={() => setDropdownOpen(false)}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            {/* Filter Input */}
            <div style={{ padding: "12px" }}>
              <input
                ref={searchInputReference}
                type="text"
                placeholder="Search by filename..."
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "3px",
                  fontSize: "14px",
                }}
                onChange={(e) => {
                  const filterValue = e.target.value.trim();
                  if (filterValue) {
                    const words = filterValue.split(" ");
                    const wordFilters = words.map((word: string) => ({
                      logic: "or",
                      filters: [
                        { field: "name", operator: "contains", value: word, ignoreCase: true },
                        { field: "createdBy", operator: "contains", value: word, ignoreCase: true },
                      ],
                    }));
                    setFilter({ logic: "and", filters: wordFilters } as CompositeFilterDescriptor);
                  } else {
                    setFilter(undefined);
                  }
                }}
              />
            </div>

            {/* Items List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              {filteredData.map((item) => {
                const isSelected = selectedSettings.some((selected) => isMatchingSetting(item, selected));
                const compatibility = getSettingsCompatibility(item);
                const isCompatible = compatibility.compatible;
                const detectedVersion = item.detectedSoftwareVersion ?? item.softwareVersion;
                return (
                  <div
                    key={item.id}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                      opacity: isCompatible ? 1 : 0.6,
                    }}
                    onClick={() => {
                      if (!isCompatible) {
                        return;
                      }

                      const currentSelected = [...selectedSettings];
                      const index = currentSelected.findIndex((selected) => isMatchingSetting(item, selected));

                      if (index === -1) {
                        if (isSingleSelection) {
                          currentSelected.splice(0, currentSelected.length, item);
                          setDropdownOpen(false);
                        } else {
                          currentSelected.push(item);
                        }
                      } else {
                        currentSelected.splice(index, 1);
                      }

                      markItemsAsLastUsed(
                        settingsLastUsedStorageKey,
                        currentSelected.map((item) => getSettingsStorageKey(item)),
                      );
                      setSelectedSettings(currentSelected);
                      setInJobStore(currentSelected);
                      setSettingsList(sortSettings(settingsList));
                    }}
                  >
                    <Checkbox checked={isSelected} style={{ marginRight: 8 }} readOnly={true} />
                    <div style={{ flex: 2, overflow: "hidden", marginRight: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</span>
                        {softwareScoped && <SettingsOriginPill software={item.software} showEngineName={showEngineName} />}
                      </div>
                      {detectedVersion && (
                        <div style={{ fontSize: "11px", color: "#666" }}>{compatibility.reason ? `Detected: ${detectedVersion} (${compatibility.reason})` : `Detected: ${detectedVersion}`}</div>
                      )}
                    </div>
                    <span style={{ flex: 1, fontSize: "0.9em", color: "#666" }}>By: {item.createdBy}</span>
                    <Button
                      svgIcon={trashIcon}
                      fillMode="flat"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm(`Are you sure you want to delete ${item.name}?`)) {
                          try {
                            await deleteSettingsService.executeAsync(item.name, settingsType);

                            // Remove from selected if present
                            const updatedSelected = selectedSettings.filter((s) => s.id !== item.id);
                            if (updatedSelected.length !== selectedSettings.length) {
                              setSelectedSettings(updatedSelected);
                              setInJobStore(updatedSelected);
                            }

                            // Update list
                            const updatedList = settingsList.filter((s) => s.id !== item.id);
                            setCachedSettingsList(settingsType, updatedList, scopedSoftware);
                            setSettingsList(updatedList);
                          } catch (error) {
                            console.error("Failed to delete settings file", error);
                            alert("Failed to delete file.");
                          }
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Resize Handle */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "20px",
                height: "20px",
                cursor: "se-resize",
                backgroundColor: "#f0f0f0",
                borderLeft: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                borderRadius: "0 0 4px 0",
              }}
              onMouseDown={handleResizeStart}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "2px",
                  right: "2px",
                  width: "0",
                  height: "0",
                  borderLeft: "8px solid transparent",
                  borderBottom: "8px solid #999",
                }}
              />
            </div>
          </div>
        </>
      )}

      {showSettingsDialog && <UploadSettingsDialog onUploadSuccess={afterSettingsUploadSuccess} settingsType={settingsType} onCancel={closeSettingsDialog} />}
    </div>
  );
};
